import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { JsonViewer } from '@/components/JsonViewer';
import { TierCard } from '@/components/matching/TierCard';
import { PageHeader, PageShell } from '@/components/PageShell';
import { TabNav } from '@/components/TabNav';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTableSort } from '@/lib/tableSort';
import {
    allBrandsState,
    allEquipmentTypesState,
    documentsReplacedByState,
    equipmentTypeFamiliesState,
    modelDocumentState,
    modelsLinkingDocumentState,
} from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { canSupersede, documentLinkFit, documentTitle, isDirectLink, supersessionRefusal } from '@rock/manuals';
import type { Brand, BrandId, EquipmentTypeFamily, EquipmentTypeId, Model, ModelDocument } from '@rock/types';
import { isCustomId, ModelDocumentIdSchema } from '@rock/types';
import { Pencil, Replace } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { type DocumentEdit, saveDocumentEdit, undoReplacement } from './documentEdits';
import {
    CoveragePills,
    DocumentBrandChips,
    DocumentLink,
    DocumentSourceLinks,
    DocumentStateBadges,
    LinkFitBadge,
    LinkKindBadge,
    MissingDocumentScope,
    PdfPreview,
    TagChips,
} from './documentShared';
import { EditDocumentDialog } from './EditDocumentDialog';
import { brandsById } from './modelBrand';
import { ModelBrandCell } from './ModelBrandCell';
import { ReplaceDocumentDialog } from './ReplaceDocumentDialog';
import { TypeFamilyLine } from './TypeFamilyLine';

const log = logger('DocumentDetailsPage');

/**
 * One confirmed document: what it is (Details) and the models linking it (Models). The document is shared,
 * so this is the page where an edit's reach is visible in full — every model a change would touch.
 */
export const DocumentDetailsPage: FC = () => {
    const { docId: rawDocId } = useParams<{ docId: string }>();
    const docId = useMemo(
        () => (rawDocId ? ModelDocumentIdSchema.decode(decodeURIComponent(rawDocId)) : undefined),
        [rawDocId],
    );
    const [doc, docLoadable] = useLoadableAtomValue(useMemo(() => modelDocumentState(docId), [docId]));

    if (!doc) {
        return (
            <PageShell>
                <PageHeader title="Document" id={docId} backLabel="Back to documents" />
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {docLoadable.state === 'loading' ? 'Loading document' : 'Document not found'}
                        </CardTitle>
                        <CardDescription>
                            {docLoadable.state === 'loading'
                                ? 'Fetching the document.'
                                : 'No document has this id. It may have been unpublished from its last model.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            </PageShell>
        );
    }
    return <LoadedDocumentPage doc={doc} />;
};

/** The models linking a document, or why they can't be shown. */
type Linking = { models: Model[] } | { state: 'loading' | 'error' };

const LoadedDocumentPage: FC<{ doc: ModelDocument }> = ({ doc }) => {
    const [models, loadable] = useLoadableAtomValue(useMemo(() => modelsLinkingDocumentState(doc.id), [doc.id]));
    const linking: Linking = models ? { models } : { state: loadable.state === 'hasError' ? 'error' : 'loading' };
    const base = `/products/documents/${encodeURIComponent(doc.id)}`;
    return (
        <PageShell>
            <PageHeader title={documentTitle(doc)} id={doc.id} backLabel="Back to documents" />
            <TabNav
                tabs={[
                    { label: 'Details', to: `${base}/details` },
                    { label: 'Models', to: `${base}/models`, count: models?.length },
                ]}
            />
            <Routes>
                <Route index element={<Navigate replace to="./details" />} />
                <Route path="details" element={<DocumentDetailsTab doc={doc} linking={linking} />} />
                <Route path="models" element={<DocumentModelsTab doc={doc} linking={linking} />} />
            </Routes>
        </PageShell>
    );
};

/** What each gate of a document's rule decides, in the order a model meets them. */
const DOCUMENT_TYPE_HINT = (
    <>
        <span>
            A model of a listed type gets this document by rule; one whose type shares a family with a listed type gets
            it too, flagged.
        </span>
        <span>A model linked to it directly under another type keeps it, flagged.</span>
    </>
);

const DOCUMENT_BRAND_HINT = (
    <>
        <span>Only models of a listed brand get this document by rule; a model with no brand never does.</span>
        <span>A model linked to it directly under another brand keeps it, flagged.</span>
    </>
);

const DOCUMENT_NUMBER_HINT = (
    <>
        <span>
            A model gets this document by rule when its whole model number starts with one of the prefixes, or falls in
            one of the ranges.
        </span>
        <span>With no rule, only the models it is linked to directly have it.</span>
    </>
);

const DocumentDetailsTab: FC<{ doc: ModelDocument; linking: Linking }> = ({ doc, linking }) => {
    const [editing, setEditing] = useState(false);
    const [replacing, setReplacing] = useState(false);
    const [saving, setSaving] = useState(false);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const typeTitles = useMemo(() => new Map(types.map((type) => [type.id, type.title])), [types]);

    const save = async (current: ModelDocument, edit: DocumentEdit): Promise<boolean> => {
        setSaving(true);
        try {
            await saveDocumentEdit(current, edit);
            toast.success('Publication updated');
            return true;
        } catch (err) {
            log.error('Saving a document edit failed', { err, docId: current.id });
            toast.error('Write failed', { description: err instanceof Error ? err.message : `${err}` });
            return false;
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="grid items-start gap-6 lg:grid-cols-[2fr_1fr]">
            <div className="flex flex-col gap-6">
                <DocumentSummaryCard doc={doc} onEdit={() => setEditing(true)} onReplace={() => setReplacing(true)} />
                <Card>
                    <CardHeader>
                        <CardTitle>Object</CardTitle>
                        <CardDescription>The document as stored in Firestore.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                            <JsonViewer data={doc} />
                        </div>
                    </CardContent>
                </Card>
            </div>
            <div className="flex flex-col gap-4">
                <TierCard title="Type" tier={1} hint={DOCUMENT_TYPE_HINT}>
                    {doc.typeIds.map((typeId) => (
                        <div key={typeId} className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <EquipmentTypeBadge typeId={typeId} title={typeTitles.get(typeId)} />
                                <span className="text-sm">
                                    {typeTitles.get(typeId) ?? (isCustomId(typeId) ? 'Custom type' : '')}
                                </span>
                            </div>
                            <TypeFamilyLine typeId={typeId} typeTitles={typeTitles} listed={doc.typeIds} />
                        </div>
                    ))}
                    {doc.typeIds.length === 0 && <MissingDocumentScope />}
                </TierCard>
                <TierCard title="Brand" tier={2} hint={DOCUMENT_BRAND_HINT}>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <DocumentBrandChips brandIds={doc.brandIds} />
                        {doc.brandIds.length === 0 && <MissingDocumentScope />}
                    </div>
                </TierCard>
                <TierCard title="Model Number" tier={3} hint={DOCUMENT_NUMBER_HINT}>
                    {doc.coverage ? (
                        <CoveragePills coverage={doc.coverage} />
                    ) : (
                        <div className="text-sm text-muted-foreground">
                            No rule: only the models it is linked to have it.
                        </div>
                    )}
                    <LinkCounts doc={doc} linking={linking} />
                </TierCard>
            </div>
            <EditDocumentDialog open={editing} onOpenChange={setEditing} doc={doc} saving={saving} onSave={save} />
            {replacing && (
                <ReplaceDocumentDialog
                    open
                    onOpenChange={setReplacing}
                    doc={doc}
                    linkedModels={'models' in linking ? linking.models : []}
                />
            )}
        </div>
    );
};

const DocumentSummaryCard: FC<{ doc: ModelDocument; onEdit: () => void; onReplace: () => void }> = ({
    doc,
    onEdit,
    onReplace,
}) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                <div className="space-y-1.5">
                    <CardTitle>Summary</CardTitle>
                    <CardDescription>What this document is; the cards beside it say who it reaches.</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={onEdit}>
                        <Pencil className="h-3.5 w-3.5" />
                        Edit publication…
                    </Button>
                    {!doc.replacedBy && (
                        <Button type="button" variant="outline" size="sm" onClick={onReplace}>
                            <Replace className="h-3.5 w-3.5" />
                            Replace with newer revision…
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                <RevisionLines doc={doc} />
                <div className="flex flex-wrap items-center gap-2">
                    <TagChips tags={doc.tags} />
                    <DocumentStateBadges doc={doc} />
                </div>
                {doc.digest && <div className="text-sm">{doc.digest.summary}</div>}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>Published {new Date(doc.createdAt).toLocaleString()}</span>
                    <DocumentSourceLinks doc={doc} />
                    <Button type="button" variant="ghost" size="sm" onClick={() => setPreviewOpen((open) => !open)}>
                        {previewOpen ? 'Hide preview' : 'Preview'}
                    </Button>
                </div>
                {previewOpen && <PdfPreview url={doc.storageUrl} title={documentTitle(doc)} />}
            </CardContent>
        </Card>
    );
};

/** Where the document sits in its revision chain: what replaced it (with Undo) and what it replaced. */
const RevisionLines: FC<{ doc: ModelDocument }> = ({ doc }) => {
    const [successor] = useLoadableAtomValue(useMemo(() => modelDocumentState(doc.replacedBy), [doc.replacedBy]));
    const [earlier] = useLoadableAtomValue(useMemo(() => documentsReplacedByState(doc.id), [doc.id]));
    const [undoing, setUndoing] = useState(false);
    const undo = async () => {
        setUndoing(true);
        try {
            await undoReplacement(doc);
            toast.success('Replacement undone', { description: 'The linked models switch back in a moment.' });
        } catch (err) {
            log.error('Undoing a replacement failed', { err, docId: doc.id });
            toast.error('Write failed', { description: err instanceof Error ? err.message : `${err}` });
        } finally {
            setUndoing(false);
        }
    };
    if (!doc.replacedBy && !earlier?.length) return null;
    // Tags can change after a replacement is made: it then supersedes nothing until the kinds line up again.
    const successorGap = successor && supersessionRefusal(successor, doc);
    const gaps = (earlier ?? []).filter((previous) => !canSupersede(doc, previous));
    return (
        <div className="space-y-1.5 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-950">
            {doc.replacedBy && (
                <div className="flex flex-wrap items-center gap-2">
                    <span>
                        Replaced by <DocumentLink id={doc.replacedBy} doc={successor} />:{' '}
                        {successorGap
                            ? `it supersedes nothing for now. ${successorGap}`
                            : 'superseded wherever that revision reaches, current everywhere else.'}
                    </span>
                    <Button type="button" variant="outline" size="sm" disabled={undoing} onClick={() => void undo()}>
                        {undoing ? 'Undoing…' : 'Undo replacement'}
                    </Button>
                </div>
            )}
            {!!earlier?.length && (
                <div>
                    Replaces{' '}
                    {earlier.map((previous, index) => (
                        <span key={previous.id}>
                            {index > 0 && ', '}
                            <DocumentLink id={previous.id} doc={previous} />
                        </span>
                    ))}
                    .
                    {gaps.length > 0 &&
                        ` Not superseding ${gaps.map(documentTitle).join(', ')} for now: it lacks their most important kind.`}
                </div>
            )}
        </div>
    );
};

/** How the document reached the models that have it. */
const LinkCounts: FC<{ doc: ModelDocument; linking: Linking }> = ({ doc, linking }) => {
    if (!('models' in linking)) return <LinkingUnavailable state={linking.state} />;
    const { models } = linking;
    if (models.length === 0) return <div className="text-sm text-muted-foreground">No model links this document.</div>;
    const direct = models.filter((model) => {
        const link = model.documents?.[doc.id];
        return !!link && isDirectLink(link);
    }).length;
    return (
        <div className="text-sm text-muted-foreground">
            Linked to {models.length} model{models.length === 1 ? '' : 's'}: {direct} directly, {models.length - direct}{' '}
            by rule.
        </div>
    );
};

const LinkingUnavailable: FC<{ state: 'loading' | 'error' }> = ({ state }) =>
    state === 'error' ? (
        <div className="text-sm text-destructive">The models linking this document could not be loaded.</div>
    ) : (
        <div className="field-hint">Loading linked models…</div>
    );

const DocumentModelsTab: FC<{ doc: ModelDocument; linking: Linking }> = ({ doc, linking }) => {
    const brands = useAsyncAtomValue(allBrandsState, []);
    const [families] = useLoadableAtomValue(equipmentTypeFamiliesState);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const { sort, setSort } = useTableSort();
    const columns = useMemo(
        () =>
            linkingModelColumns(doc, brandsById(brands), new Map(types.map((type) => [type.id, type.title])), families),
        [doc, brands, types, families],
    );
    return (
        <Card>
            <CardHeader>
                <CardTitle>Models</CardTitle>
                <CardDescription>
                    Every model linking this document: directly, by a person, or by its rule. Tap a row to open it.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {!('models' in linking) ? (
                    <LinkingUnavailable state={linking.state} />
                ) : linking.models.length === 0 ? (
                    <div className="section-description">No model links this document.</div>
                ) : (
                    <DataTable
                        columns={columns}
                        rows={linking.models}
                        rowKey={(model) => model.id}
                        rowHref={(model) => `/products/models/${encodeURIComponent(model.id)}`}
                        sort={sort}
                        onSortChange={setSort}
                    />
                )}
            </CardContent>
        </Card>
    );
};

/** `families` is undefined while it loads: a fit tag then would call a family match "other type". */
function linkingModelColumns(
    doc: ModelDocument,
    brandLookup: Map<BrandId, Brand>,
    typeTitles: Map<EquipmentTypeId, string>,
    families: readonly EquipmentTypeFamily[] | undefined,
): DataTableColumn<Model>[] {
    return [
        {
            key: 'model',
            header: 'Model',
            className: 'min-w-[160px]',
            cellClassName: 'font-medium',
            sortBy: (model) => model.modelCanonical,
            cell: (model) => model.modelCanonical,
        },
        {
            key: 'brand',
            header: 'Brand',
            className: 'min-w-[160px]',
            cell: (model) => <ModelBrandCell model={model} brandLookup={brandLookup} />,
        },
        {
            key: 'type',
            header: 'Type',
            className: 'min-w-[140px]',
            cell: (model) => <EquipmentTypeBadge typeId={model.typeId} title={typeTitles.get(model.typeId)} />,
        },
        {
            key: 'link',
            header: 'Link',
            className: 'min-w-[200px]',
            cell: (model) => {
                const link = model.documents?.[doc.id];
                return (
                    <span className="flex flex-wrap items-center gap-1.5">
                        {link && <LinkKindBadge direct={isDirectLink(link)} />}
                        {families && <LinkFitBadge fit={documentLinkFit(doc, model, families)} />}
                        {link?.supersededBy && (
                            <Badge variant="secondary" className="border-transparent bg-orange-100 text-orange-900">
                                {link.supersededVia === 'replaced' ? 'Superseded by a newer revision' : 'Superseded'}
                            </Badge>
                        )}
                    </span>
                );
            },
        },
    ];
}
